import type { UserRole, UserStatus } from "@/types";

import type { EnrollmentStatus } from "../enrollments/types";

/**
 * Hisobot tiplari — backend'dagi `reports/dto/report-response.dto.ts` ning
 * ko'zgusi. Sanalar simda ISO satr bo'lib keladi.
 */

export type CourseKind = "LIVE" | "RECORDED";

export type AtRiskReason =
  | "LOW_ATTENDANCE"
  | "LOW_GRADES"
  | "LOW_TASK_SCORES"
  | "OVERDUE_TASKS"
  | "INACTIVE"
  | "STALLED_PROGRESS";

export const AT_RISK_REASONS: readonly AtRiskReason[] = [
  "LOW_ATTENDANCE",
  "LOW_GRADES",
  "LOW_TASK_SCORES",
  "OVERDUE_TASKS",
  "INACTIVE",
  "STALLED_PROGRESS",
];

export type ModerationSubject = "COURSE" | "POST" | "REVIEW" | "TEACHER_REQUEST";

export const MODERATION_SUBJECTS: readonly ModerationSubject[] = [
  "COURSE",
  "POST",
  "REVIEW",
  "TEACHER_REQUEST",
];

export type ModerationDecision = "APPROVED" | "REJECTED";

export interface ReportWindow {
  /** `YYYY-MM-DD`, Toshkent kuni boshidan. */
  from?: string;
  /** `YYYY-MM-DD`, Toshkent kuni oxirigacha. */
  to?: string;
}

export interface ReportRange {
  from: string;
  to: string;
}

export interface ReportUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string | null;
  role: UserRole;
  status: UserStatus;
  createdAt: string;
  lastActiveAt: string | null;
  lastLoginAt: string | null;
}

export interface PersonRef {
  id: number;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
}

/**
 * Pulning bo'linishi.
 *
 * `net = received − refunded`, `teacherShare = net − platformFee`. Ulush
 * har bir to'lovda o'sha paytdagi foiz bilan muzlatilgan — sozlama keyin
 * o'zgarsa ham eski to'lovlar qayta hisoblanmaydi.
 */
export interface MoneySplit {
  receivedUzs: number;
  refundedUzs: number;
  netUzs: number;
  platformFeeUzs: number;
  teacherShareUzs: number;
  paymentCount: number;
  refundCount: number;
}

export interface SessionCounts {
  held: number;
  cancelled: number;
  /** Vaqti o'tgan, lekin o'tkazilmagan dars. */
  missed: number;
  upcoming: number;
  /** O'tkazilgan, lekin hech kimga baho qo'yilmagan. */
  ungraded: number;
  /** O'tkazilgan darslarda o'qituvchi: o'z vaqtida / kechikib / kirmagan / ma'lumot yo'q. */
  teacherOnTime: number;
  teacherLate: number;
  teacherAbsent: number;
  presenceUnknown: number;
}

export type TeacherRiskReason =
  | "MISSED_SESSIONS"
  | "UNGRADED_SESSIONS"
  | "REVIEW_BACKLOG"
  | "SLOW_GRADING"
  | "UNANSWERED_QUESTIONS"
  | "STUDENTS_AT_RISK";

export const TEACHER_RISK_REASONS: readonly TeacherRiskReason[] = [
  "MISSED_SESSIONS",
  "UNGRADED_SESSIONS",
  "REVIEW_BACKLOG",
  "SLOW_GRADING",
  "UNANSWERED_QUESTIONS",
  "STUDENTS_AT_RISK",
];

/** Tekshiruv sanog'i: oynada tekshirilgan, hozir kutayotgan va muddati o'tgan. */
export interface ReviewCounts {
  graded: number;
  pending: number;
  overdue: number;
}

/** Backend'dagi `TEACHER_RISK_RULES`. */
export interface TeacherRiskRules {
  missedSessions: number;
  lateMinutes: number;
  ungradedSessions: number;
  reviewOverdueHours: number;
  overdueReviews: number;
  slowGradingHours: number;
  minGradedForSpeed: number;
  questionOverdueHours: number;
  unansweredQuestions: number;
  studentsAtRiskPercent: number;
  minStudentsAtRisk: number;
}

export type TeacherPresence = "ON_TIME" | "LATE" | "ABSENT" | "UNKNOWN";

/** O'tgan jonli dars va unda o'qituvchi bo'lganmi. */
export interface TeacherSessionPresence {
  sessionId: number;
  courseId: number;
  courseTitle: string;
  date: string;
  startTime: string;
  title: string;
  kind: string;
  /** MISSED — vaqti o'tgan, lekin platformada boshlanmagan. */
  status: "ENDED" | "MISSED";
  presence: TeacherPresence;
  teacherJoinedAt: string | null;
  lateMinutes: number | null;
  attended: number;
  expected: number;
  graded: boolean;
}

/** Tekshiruvni kutayotgan ish. */
export interface PendingReview {
  kind: "ASSIGNMENT" | "QUIZ";
  id: number;
  courseId: number;
  courseTitle: string;
  courseType: CourseKind;
  lessonId: number;
  title: string;
  student: PersonRef;
  submittedAt: string;
  waitingHours: number;
  overdue: boolean;
}

export interface RatingSummary {
  average: number | null;
  count: number;
}

export type MarkDistribution = Record<"5" | "4" | "3" | "2", number>;

export interface TaskCounts {
  PENDING: number;
  OVERDUE: number;
  SUBMITTED: number;
  GRADED: number;
  RETURNED: number;
  total: number;
}

// ─── O'qituvchilar ───────────────────────────────────────────────────────────

export interface TeacherReportRow {
  teacher: ReportUser;
  courses: {
    total: number;
    published: number;
    live: number;
    recorded: number;
    pendingReview: number;
  };
  students: { active: number; total: number; newInRange: number };
  money: MoneySplit;
  sessions: SessionCounts;
  attendancePercent: number | null;
  averagePercent: number | null;
  pendingReviews: number;
  gradedInRange: number;
  avgGradingHours: number | null;
  openQuestions: number;
  rating: RatingSummary;
  atRiskStudents: number;
  actionsInRange: number;
  /** Tekshiruv — kurs turi bo'yicha. */
  reviews: { live: ReviewCounts; recorded: ReviewCounts };
  /** 48 soatdan ortiq javobsiz savollar. */
  staleQuestions: number;
  /** O'qituvchining o'z xavfi. */
  risk: TeacherRiskReason[];
  /** O'qituvchi reytingi: 0–100 ball va 6 qism (ma'lumoti yo'q qism — `null`). */
  score: TeacherScore;
}

export const SCORE_COMPONENTS = [
  "rating",
  "attendance",
  "grades",
  "gradingSpeed",
  "retention",
  "responsiveness",
] as const;
export type ScoreComponent = (typeof SCORE_COMPONENTS)[number];

export interface TeacherScore {
  score: number | null;
  components: Record<ScoreComponent, number | null>;
}

export const QUALITY_FLAGS = [
  "LOW_RATING",
  "LOW_COMPLETION",
  "LOW_ATTENDANCE",
  "LOW_GRADES",
  "HIGH_REFUNDS",
  "REVIEW_BACKLOG",
  "MANY_AT_RISK",
] as const;
export type QualityFlag = (typeof QUALITY_FLAGS)[number];

export interface CourseQuality {
  score: number;
  flags: QualityFlag[];
}

export interface MoneyPoint {
  /** Bo'lak boshi, `YYYY-MM-DD`. */
  bucket: string;
  netUzs: number;
  teacherShareUzs: number;
}

export interface AttendancePoint {
  /** Hafta boshi (dushanba), `YYYY-MM-DD`. */
  bucket: string;
  /** Shu haftada dars bo'lmagan bo'lsa `null`. */
  percent: number | null;
  sessions: number;
}

export interface TeacherAnalytics {
  range: ReportRange;
  commissionBps: number;
  summary: TeacherReportRow;
  moneySeries: { granularity: "day" | "week" | "month"; points: MoneyPoint[] };
  attendanceSeries: AttendancePoint[];
  gradeDistribution: MarkDistribution;
  courses: CourseReportRow[];
  atRisk: AtRiskRow[];
  riskRules: TeacherRiskRules;
  /** Oynadagi o'tgan jonli darslar — yangisidan. */
  presence: TeacherSessionPresence[];
  /** Tekshiruvni kutayotgan ishlar — eskisidan. */
  reviewQueue: PendingReview[];
}

// ─── Kurslar ────────────────────────────────────────────────────────────────

export const REPORT_COURSE_STATUSES = [
  "DRAFT",
  "PENDING_REVIEW",
  "PUBLISHED",
  "REJECTED",
  "ARCHIVED",
] as const;
export type ReportCourseStatus = (typeof REPORT_COURSE_STATUSES)[number];

export interface CourseReportRow {
  course: {
    id: number;
    title: string;
    type: CourseKind;
    status: ReportCourseStatus;
    createdAt: string;
    publishedAt: string | null;
    finishedAt: string | null;
  };
  teacher: PersonRef;
  students: { active: number; completed: number; total: number; newInRange: number };
  completionRate: number | null;
  /** Faqat video kursda; jonli kursda `null`. */
  avgProgress: number | null;
  attendancePercent: number | null;
  averagePercent: number | null;
  money: MoneySplit;
  rating: RatingSummary;
  /** Faqat jonli kursda. */
  sessions: SessionCounts | null;
  pendingReviews: number;
  atRiskStudents: number;
  /** Kurs sifati: 0–100 ball va muammo belgilari. */
  quality: CourseQuality;
}

export interface FunnelStep {
  lessonId: number;
  title: string;
  type: string;
  started: number;
  completed: number;
  completedPercent: number | null;
}

/** Darslar bo'ylab: nechtasi boshlagan va tugatgan, qayerda to'xtaydi. */
export interface CourseFunnel {
  students: number;
  steps: FunnelStep[];
  dropOffLessonId: number | null;
}

// ─── Kogortalar ─────────────────────────────────────────────────────────────

export interface CohortRow {
  /** "2026-04" */
  cohort: string;
  size: number;
  completed: number;
  completionRate: number | null;
  /** M0..M5 — faol qolganlar ulushi, %; hali kelmagan oy — `null`. */
  retention: (number | null)[];
}

export interface CohortQuery {
  months?: number;
  type?: CourseKind;
  courseId?: number;
  teacherId?: number;
}

// ─── O'qituvchilarga to'lovlar ──────────────────────────────────────────────

export interface PayoutRecord {
  id: number;
  period: string;
  amountUzs: number;
  netUzs: number;
  platformFeeUzs: number;
  paymentCount: number;
  reference: string | null;
  paidAt: string;
  paidBy: PersonRef | null;
}

export interface PayoutRow {
  teacher: PersonRef;
  money: MoneySplit;
  payout: PayoutRecord | null;
  /** Qayd etilgandan keyingi farq (hozirgi ulush − to'langan); qayd yo'q — `null`. */
  differenceUzs: number | null;
}

export interface PayoutMonth {
  period: string;
  /** Oy tugaganmi — to'lov faqat tugagan oy uchun qayd etiladi. */
  closed: boolean;
  rows: PayoutRow[];
  totals: { teacherShareUzs: number; paidUzs: number; unpaidUzs: number };
}

// ─── Xavf ostidagilar ───────────────────────────────────────────────────────

export interface AtRiskRow {
  student: PersonRef;
  email: string;
  courseId: number;
  courseTitle: string;
  courseType: CourseKind;
  teacher: PersonRef;
  reasons: AtRiskReason[];
  attendancePercent: number | null;
  expectedSessions: number;
  averagePercent: number | null;
  gradedCount: number;
  overdueTasks: number;
  /** Baholangan topshiriq va testlarning o'rtacha natijasi (foiz). */
  taskAveragePercent: number | null;
  taskGradedCount: number;
  /** Faqat video kursda. */
  progress: number | null;
  lastActivityAt: string | null;
  /** Video kursda oxirgi tugatilgan dars (yo'q bo'lsa — qo'shilgan payt). */
  lastProgressAt: string | null;
}

// ─── Talabalar ──────────────────────────────────────────────────────────────

export interface StudentReportRow {
  student: ReportUser;
  courses: { active: number; completed: number; total: number };
  avgProgress: number | null;
  attendancePercent: number | null;
  averagePercent: number | null;
  /** 5 ballik baho (2–5). */
  averageMark: number | null;
  quizAveragePercent: number | null;
  tasks: TaskCounts;
  paidUzs: number;
  certificates: number;
  risk: AtRiskReason[];
  actionsInRange: number;
}

export interface StudentCourseRank {
  position: number | null;
  total: number;
  basis: "FINAL" | "AVERAGE";
}

export type SessionAttendance = "PRESENT" | "LATE" | "ABSENT" | "EXCUSED";

/** Talabaning bitta o'tkazilgan darsi — davomat belgisi va kunlik bahosi. */
export interface StudentSessionRow {
  sessionId: number;
  /** `YYYY-MM-DD` */
  date: string;
  /** `HH:mm:ss` */
  startTime: string;
  title: string;
  kind: string;
  /** `null` — belgi qo'yilmagan (hisobga kirsa, "kelmadi" hisoblanadi). */
  attendance: SessionAttendance | null;
  note: string | null;
  /** Davomat foiziga kiradimi (qo'shilishidan oldingi belgisiz dars — yo'q). */
  counted: boolean;
  score: number | null;
  maxScore: number | null;
  percent: number | null;
  mark: number | null;
}

export type StudentTaskStatus = "PENDING" | "OVERDUE" | "SUBMITTED" | "GRADED" | "RETURNED";

/** Talabaning bitta topshirig'i yoki testi — holati va natijasi bilan. */
export interface StudentTask {
  lessonId: number;
  title: string;
  type: "ASSIGNMENT" | "QUIZ";
  dueDate: string | null;
  status: StudentTaskStatus;
  /** Topshiriqda — ball, testda — foiz. */
  score: number | null;
  maxScore: number;
  percent: number | null;
  submittedAt: string | null;
}

/** Xavf chegaralari — backend'dagi `AT_RISK_RULES`. */
export interface RiskRules {
  attendanceBelow: number;
  minExpectedSessions: number;
  minGradedSessions: number;
  minGradedTasks: number;
  overdueTasks: number;
  inactiveDays: number;
  stalledDays: number;
}

export interface StudentCourseDetail {
  courseId: number;
  title: string;
  type: CourseKind;
  teacher: PersonRef;
  enrollmentStatus: EnrollmentStatus;
  progress: number;
  joinedAt: string;
  lastAccessedAt: string | null;
  completedAt: string | null;
  finishedAt: string | null;
  attendancePercent: number | null;
  expectedSessions: number;
  present: number;
  late: number;
  excused: number;
  absent: number;
  averagePercent: number | null;
  averageMark: number | null;
  gradedCount: number;
  finalScore: number | null;
  finalMark: number | null;
  passed: boolean | null;
  tasks: TaskCounts;
  rank: StudentCourseRank | null;
  risk: AtRiskReason[];
  /** Xavf qoidasi qaraydigan oxirgi faollik. */
  lastActivityAt: string | null;
  sessions: StudentSessionRow[];
  /** Baholangan topshiriq va testlar o'rtachasi (foiz) va 5 ballik bahosi. */
  taskAveragePercent: number | null;
  taskAverageMark: number | null;
  taskGradedCount: number;
  /** Video kursda progress oxirgi o'sgan payt. */
  lastProgressAt: string | null;
  /** Muddati bo'yicha, muddatsizlari oxirida. */
  taskList: StudentTask[];
}

export interface GradePoint {
  date: string;
  percent: number;
  courseId: number;
  sessionTitle: string;
}

export interface StudentPayment {
  id: number;
  courseTitle: string;
  amountUzs: number;
  /** Payme holati: 2 — to'langan, -2 — to'langandan keyin qaytarilgan. */
  state: number;
  provider: string;
  performedAt: string | null;
}

export interface StudentAnalytics {
  student: ReportUser;
  summary: StudentReportRow;
  courses: StudentCourseDetail[];
  gradeTrend: GradePoint[];
  payments: StudentPayment[];
  riskRules: RiskRules;
}

// ─── Moderatorlar ───────────────────────────────────────────────────────────

export interface QueueHealthItem {
  type: ModerationSubject;
  open: number;
  /** 48 soatdan ortiq kutayotganlar. */
  overdue: number;
  oldestQueuedAt: string | null;
}

export interface ModeratorReportRow {
  moderator: ReportUser;
  decisions: { total: number; approved: number; rejected: number };
  byType: Record<ModerationSubject, number>;
  avgWaitHours: number | null;
  medianWaitHours: number | null;
  overdueDecisions: number;
  lastDecisionAt: string | null;
  actionsInRange: number;
}

export interface ModeratorReport {
  range: ReportRange;
  overdueHours: number;
  queue: QueueHealthItem[];
  moderators: ModeratorReportRow[];
}

export interface ModerationAction {
  id: number;
  subjectType: ModerationSubject;
  subjectId: number;
  subjectTitle: string;
  decision: ModerationDecision;
  reason: string | null;
  queuedAt: string | null;
  waitSeconds: number | null;
  createdAt: string;
  moderator: PersonRef;
}

export interface WeeklyRunResult {
  week: { from: string; to: string };
  skipped: boolean;
  teachers: number;
  admins: number;
  moderators: number;
}

// ─── So'rovlar ──────────────────────────────────────────────────────────────

export type SortOrder = "ASC" | "DESC";

export const TEACHER_SORTS = [
  "name",
  "students",
  "netUzs",
  "teacherShareUzs",
  "rating",
  "attendancePercent",
  "sessionsHeld",
  "pendingReviews",
  "atRisk",
  "risk",
  "lastActiveAt",
  "score",
] as const;
export type TeacherSort = (typeof TEACHER_SORTS)[number];

export const STUDENT_SORTS = [
  "name",
  "createdAt",
  "lastActiveAt",
  "courses",
  "avgProgress",
  "attendancePercent",
  "averagePercent",
  "overdueTasks",
  "paidUzs",
] as const;
export type StudentSort = (typeof STUDENT_SORTS)[number];

export const COURSE_SORTS = [
  "title",
  "createdAt",
  "activeStudents",
  "netUzs",
  "rating",
  "attendancePercent",
  "averagePercent",
  "completionRate",
  "avgProgress",
  "atRisk",
  "quality",
] as const;
export type CourseSort = (typeof COURSE_SORTS)[number];

interface ListQuery<TSort extends string> extends ReportWindow {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: TSort;
  order?: SortOrder;
}

export interface TeacherReportQuery extends ListQuery<TeacherSort> {
  status?: UserStatus;
  /** Faqat o'zi xavf ostidagi o'qituvchilar. */
  atRisk?: boolean;
  risk?: TeacherRiskReason;
}

export interface StudentReportQuery extends ListQuery<StudentSort> {
  status?: UserStatus;
  atRisk?: boolean;
  courseId?: number;
  teacherId?: number;
}

export interface CourseReportQuery extends ListQuery<CourseSort> {
  type?: CourseKind;
  status?: ReportCourseStatus;
  teacherId?: number;
}

export interface AtRiskQuery {
  page?: number;
  limit?: number;
  courseId?: number;
  teacherId?: number;
  reason?: AtRiskReason;
}

export interface ModerationActionsQuery extends ReportWindow {
  page?: number;
  limit?: number;
  subjectType?: ModerationSubject;
  decision?: ModerationDecision;
}
